import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="chat"
export default class extends Controller {
  static targets = ["inner", "input", "composer", "header"];
  static values = {
    scrollable: { type: Boolean, default: false },
  };

  connect() {
    this.keyboardOpen = false;
    this.forceScrollUntil = 0;

    this.viewportHandler = () => {
      this.keyboardOpen =
        window.visualViewport.height < window.innerHeight * 0.75;
      this.syncViewport();
    };

    if (this.hasInnerTarget && this.scrollableValue) {
      const contentElement = this.innerTarget;

      this.resizeObserver = new ResizeObserver(() => {
        this.syncViewport();
      });

      this.resizeObserver.observe(contentElement);

      this.mutationObserver = new MutationObserver(() => {
        if (this.shouldAutoScroll()) this.scrollToBottom({ force: true });
      });

      const config = {
        childList: true,
        subtree: true,
        characterData: true,
      };

      this.mutationObserver.observe(contentElement, config);

      this.scrollToBottom({ force: true });
    }

    if (this.hasComposerTarget) {
      this.composerResizeObserver = new ResizeObserver(() => {
        this.syncViewport();
      });
      this.composerResizeObserver.observe(this.composerTarget);

      this.composerMutationObserver = new MutationObserver(() => {
        this.syncViewportAndScroll();
      });
      this.composerMutationObserver.observe(this.composerTarget, {
        childList: true,
        subtree: true,
      });
    }

    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", this.viewportHandler);
      window.visualViewport.addEventListener("scroll", this.viewportHandler);
    }

    if (this.scrollableValue) {
      requestAnimationFrame(() => {
        this.syncViewport();
        this.scrollToBottom({ force: true });
      });
    }
  }

  disconnect() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }

    if (this.composerResizeObserver) {
      this.composerResizeObserver.disconnect();
    }

    if (this.composerMutationObserver) {
      this.composerMutationObserver.disconnect();
    }

    if (window.visualViewport && this.viewportHandler) {
      window.visualViewport.removeEventListener("resize", this.viewportHandler);
      window.visualViewport.removeEventListener("scroll", this.viewportHandler);
    }

    this.resetViewport();
  }

  shouldStickToBottom() {
    if (!this.hasInnerTarget) return false;
    if (Date.now() < this.forceScrollUntil) return true;
    if (this.hasInputTarget && document.activeElement === this.inputTarget) return true;

    return this.isNearBottom(this.innerTarget);
  }

  shouldAutoScroll() {
    if (!this.hasInnerTarget) return false;
    if (Date.now() < this.forceScrollUntil) return true;

    return this.isNearBottom(this.innerTarget);
  }

  isNearBottom(element) {
    return element.scrollHeight - element.scrollTop - element.clientHeight < 120;
  }

  scroll() {
    setTimeout(() => {
      this.scrollToBottom({ force: true });
    }, 500);
  }

  scrollToBottom({ force = false } = {}) {
    if (!this.hasInnerTarget) return;
    if (!force && !this.shouldAutoScroll()) return;

    const element = this.innerTarget;
    const scroll = () => {
      element.scrollTop = element.scrollHeight;
    };

    scroll();
    requestAnimationFrame(scroll);
    requestAnimationFrame(() => requestAnimationFrame(scroll));
    window.setTimeout(scroll, 50);
    window.setTimeout(scroll, 150);
    window.setTimeout(scroll, 300);
  }

  scrollInnerToBottom() {
    this.scrollToBottom({ force: true });
  }

  preserveInputFocus() {
    // Keep the text field focused so iOS Safari does not dismiss the keyboard on send.
  }

  submitEnd(e) {
    if (!e.detail.success || !this.hasInputTarget) return;

    this.forceScrollUntil = Date.now() + 600;
    this.inputTarget.value = "";

    requestAnimationFrame(() => {
      this.syncViewport();
      this.scrollToBottom({ force: true });
      this.focusInput();
    });
  }

  preventEmptySubmit(e) {
    if (!this.hasInputTarget || this.inputTarget.value.trim()) return;

    e.preventDefault();
  }

  focusInput() {
    if (!this.hasInputTarget) return;

    try {
      this.inputTarget.focus({ preventScroll: true });
    } catch (_error) {
      this.inputTarget.focus();
    }
  }

  inputFocused() {
    this.forceScrollUntil = Date.now() + 800;
    this.syncViewport();
  }

  syncViewportAndScroll() {
    requestAnimationFrame(() => {
      this.syncViewport({ forceBottom: true });
    });
  }

  syncViewport({ forceBottom = false } = {}) {
    if (window.innerWidth >= 1280) {
      this.resetViewport();
      return;
    }

    if (!window.visualViewport || !this.hasInnerTarget || !this.hasComposerTarget) {
      return;
    }

    const viewport = window.visualViewport;
    const headerHeight = this.hasHeaderTarget ? this.headerTarget.offsetHeight : 0;
    const inner = this.innerTarget;
    const scrollTop = inner.scrollTop;
    const stickToBottom = forceBottom || this.shouldStickToBottom();

    this.composerTarget.style.height = "auto";
    const composerHeight = this.composerTarget.offsetHeight;

    this.setFixed(this.headerTarget, {
      top: viewport.offsetTop,
      height: headerHeight,
    });

    this.setFixed(inner, {
      top: viewport.offsetTop + headerHeight,
      height: Math.max(0, viewport.height - headerHeight - composerHeight),
    });

    this.setFixed(this.composerTarget, {
      top: viewport.offsetTop + viewport.height - composerHeight,
      height: composerHeight,
    });

    if (stickToBottom) {
      this.scrollToBottom({ force: true });
    } else if (scrollTop > 0) {
      requestAnimationFrame(() => {
        inner.scrollTop = scrollTop;
      });
    }
  }

  setFixed(element, { top, height }) {
    if (!element) return;

    element.style.position = "fixed";
    element.style.left = "0";
    element.style.right = "0";
    element.style.top = `${top}px`;
    element.style.height = `${height}px`;
    element.style.bottom = "auto";
  }

  resetViewport() {
    [
      this.hasHeaderTarget ? this.headerTarget : null,
      this.hasInnerTarget ? this.innerTarget : null,
      this.hasComposerTarget ? this.composerTarget : null,
    ].forEach((element) => {
      if (!element) return;

      element.style.position = "";
      element.style.left = "";
      element.style.right = "";
      element.style.top = "";
      element.style.height = "";
      element.style.bottom = "";
    });
  }
}
